import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../lib/api';
import { RegistrationLockError, googleNonce, verifyGoogle } from '../lib/auth';
import { loadGoogleIdentity } from '../lib/googleIdentity';
import { currentLocale } from '../lib/i18n';
import type { Session } from '../lib/session';

/** Google's button is at most 400px wide and refuses less than 200. */
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

type Failure = 'off' | 'refused' | 'throttled' | 'app_check' | 'network' | 'failed';

/** Read a failed nonce or verify (`auth_phone/google_auth.py`, `social.py`). */
function readGoogleFailure(err: unknown): Failure {
  if (!(err instanceof ApiError)) {
    return err instanceof TypeError || (err as Error)?.name === 'AbortError' ? 'network' : 'failed';
  }
  const body = (err.body && typeof err.body === 'object' ? err.body : {}) as Record<string, unknown>;
  const code = typeof body.code === 'string' ? body.code : '';
  if (code === 'google_login_off' || err.status === 404) return 'off';
  if (code === 'age_too_young' || code === 'age_blocked') return 'refused';
  if (code === 'app_check_failed') return 'app_check';
  if (err.status === 429) return 'throttled';
  return 'failed';
}

/**
 * "Continue with Google" on the web: Google's own button (the only part of
 * Google's library that hands back an ID token, `lib/googleIdentity.ts`),
 * carrying a nonce this server issued, and the token exchanged at
 * `/v1/auth/google/verify/` - the endpoint the Android app uses, with the
 * same answers: a session, the PIN step (`onLock`), the age refusal.
 *
 * A nonce is single use, so every attempt that comes back refused fetches a
 * fresh one and draws the button again; a failed attempt never leaves a
 * button that can only fail a second time.
 */
export function GoogleSignIn({
  onSignedIn,
  onLock,
  onRefused,
  onSwitchOff,
}: {
  onSignedIn: (s: Session) => void;
  onLock: (challenge: string, retryAfter: number) => void;
  onRefused: () => void;
  /** The server answered that Google sign-in is off: re-read the methods. */
  onSwitchOff?: () => void;
}) {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const [round, setRound] = useState(0);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Google calls back long after the render that set it up: read the
  // latest handlers through a ref, never through that render's closure.
  const handlers = useRef({ onSignedIn, onLock, onRefused, onSwitchOff, t });
  handlers.current = { onSignedIn, onLock, onRefused, onSwitchOff, t };

  const say = (failure: Failure) => {
    const tr = handlers.current.t;
    switch (failure) {
      case 'off':
        handlers.current.onSwitchOff?.();
        return tr('auth.web.googleOff', {
          defaultValue: "Google sign-in isn't available right now. Use another way in.",
        });
      case 'throttled':
        return tr('auth.email.tooMany', {
          defaultValue: 'Too many attempts. Wait a little before trying again.',
        });
      case 'app_check':
        return tr('auth.web.appCheckFailed', {
          defaultValue: "This browser can't sign in right now. Use the Nsibi app, or try again later.",
        });
      case 'network':
        return tr('auth.email.network', {
          defaultValue: "Can't reach the server. Check your connection and try again.",
        });
      default:
        return tr('auth.web.googleFailed', {
          defaultValue: "Couldn't sign in with Google. Try again.",
        });
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    setReady(false);

    (async () => {
      try {
        const [{ nonce, clientId }, gis] = await Promise.all([
          googleNonce(ac.signal),
          loadGoogleIdentity(),
        ]);
        if (ac.signal.aborted || !box.current) return;

        gis.accounts.id.initialize({
          client_id: clientId,
          nonce,
          ux_mode: 'popup',
          auto_select: false,
          itp_support: true,
          use_fedcm_for_button: true,
          callback: async ({ credential }) => {
            if (!credential) return;
            setBusy(true);
            setError(null);
            try {
              const { session } = await verifyGoogle(credential);
              handlers.current.onSignedIn(session);
            } catch (err) {
              if (err instanceof RegistrationLockError && err.challenge) {
                handlers.current.onLock(err.challenge, err.retryAfterSeconds);
                return;
              }
              const failure = readGoogleFailure(err);
              if (failure === 'refused') {
                handlers.current.onRefused();
                return;
              }
              setError(say(failure));
              // The nonce may be spent: the next try needs a new one.
              if (failure !== 'off') setRound((n) => n + 1);
            } finally {
              setBusy(false);
            }
          },
        });

        const width = Math.round(
          Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, box.current.offsetWidth || MAX_WIDTH)),
        );
        box.current.replaceChildren();
        gis.accounts.id.renderButton(box.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'center',
          width,
          locale: currentLocale(),
        });
        setReady(true);
      } catch (err) {
        if (ac.signal.aborted || (err as Error)?.name === 'AbortError') return;
        setError(say(readGoogleFailure(err)));
      }
    })();

    return () => ac.abort();
  }, [round]);

  return (
    <div className="w-full">
      <div className="relative flex h-10 w-full justify-center">
        {/* Google's iframe goes in here; until it is drawn, a placeholder of
            the same size keeps the buttons under it from jumping. */}
        <div ref={box} className="flex w-full justify-center [color-scheme:light]" />
        {!ready && !error ? (
          <div className="absolute inset-0 rounded-full border border-border bg-background" aria-hidden />
        ) : null}
        {busy ? (
          <div className="absolute inset-0 grid place-items-center rounded-full bg-background/80">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        ) : null}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-center text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
